import React, { useState } from 'react';
import { ArrowLeft, Briefcase, Camera, Save, Building2, Globe, Mail, Phone, FileText } from 'lucide-react';
import { useApp } from '../context/AppContext';

const CompanySettingsPage = ({ onBack, onNavigate }) => {
  const { currentCompany, user } = useApp();
  
  const [formData, setFormData] = useState({
    name: currentCompany?.name || '',
    description: currentCompany?.description || 'Descrizione dell\'azienda',
    email: user?.email || '',
    phone: '+39 081 123 4567',
    website: '',
    vatNumber: 'IT12345678901',
    fiscalCode: 'RSSMRA80A01H501U'
  });
  
  const [logo, setLogo] = useState(currentCompany?.logo || null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    // Simulate save
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSaving(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-600 to-slate-700 pt-6 pb-8">
        <div className="max-w-screen-xl mx-auto px-6">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            data-testid="company-settings-back"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Indietro</span>
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Info Azienda</h1>
              <p className="text-slate-200">Impostazioni generali dell'azienda</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-6 -mt-4">
        {/* Logo Section */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <h2 className="font-semibold text-gray-900 mb-4">Logo Azienda</h2>
          <div className="flex items-center gap-6">
            <div className="relative">
              {logo ? (
                <img src={logo} alt="Logo" className="w-24 h-24 rounded-2xl object-cover" />
              ) : (
                <div className="w-24 h-24 bg-gray-100 rounded-2xl flex items-center justify-center">
                  <Building2 className="w-10 h-10 text-gray-400" />
                </div>
              )}
              <button className="absolute -bottom-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-emerald-600 transition-colors">
                <Camera className="w-4 h-4" />
              </button>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Il logo viene mostrato su tutte le sedi dell'azienda</p>
              <p className="text-xs text-gray-400">Formato: JPG, PNG. Max 2MB</p>
            </div>
          </div>
        </div>

        {/* Company Info */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <h2 className="font-semibold text-gray-900 mb-4">Informazioni Generali</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome Azienda</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder="Nome dell'azienda"
                data-testid="company-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                placeholder="Breve descrizione dell'azienda"
                data-testid="company-description-input"
              />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <h2 className="font-semibold text-gray-900 mb-4">Contatti</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Mail className="w-4 h-4 inline mr-2" />
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder="email@azienda.it"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Phone className="w-4 h-4 inline mr-2" />
                Telefono
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder="+39 000 000 0000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Globe className="w-4 h-4 inline mr-2" />
                Sito Web
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder="https://www.azienda.it"
              />
            </div>
          </div>
        </div>

        {/* Fiscal Info */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <h2 className="font-semibold text-gray-900 mb-4">
            <FileText className="w-5 h-5 inline mr-2" />
            Dati Fiscali
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Partita IVA</label>
              <input
                type="text"
                value={formData.vatNumber}
                onChange={(e) => setFormData({...formData, vatNumber: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder="IT00000000000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
              <input
                type="text"
                value={formData.fiscalCode}
                onChange={(e) => setFormData({...formData, fiscalCode: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder="XXXXXXXXXXXXXXXX"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full bg-emerald-500 text-white py-4 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors disabled:opacity-50"
          data-testid="company-settings-save"
        >
          <Save className="w-5 h-5" />
          {isSaving ? 'Salvataggio...' : 'Salva Modifiche'}
        </button>
      </div>
    </div>
  );
};

export default CompanySettingsPage;
